"use client";

import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "../../../style/cssModules/table/TableComponent.module.css";
import actionIcon from "../../images/icons/Action.png";
import PermissionContextFunctions from "../../functions/PermissionContextFunctions";
import { useTranslation } from "react-i18next";
import useRTLContext from "../../../hooks/useRTLContext";

const HeaderActionItem = ({
  currentUserId = "",
  data,
  handleBulkStatusUpdate,
  permissionName,
  statusPath = "status",
  isDelete = true,
  isInvite = false,
  handleSendInvitation,
  invitationLabel = "",
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const { grantPermission } = PermissionContextFunctions();
  const threeDotsRef = useRef(null);
  const dropdownRef = useRef(null);
  const [openHeaderMenu, setOpenHeaderMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  // Helper function to get status from nested path if needed
  const getStatus = (row) => {
    if (statusPath === "status") return row.status;

    return statusPath.split(".").reduce((obj, key) => {
      return obj && obj[key] !== undefined ? obj[key] : undefined;
    }, row);
  };

  // Get all selected rows
  const selectedRows = data.filter((row) => row.isSelected);
  const selectedCount = selectedRows.length;
  const hasSelection = selectedRows.length > 0;

  // Check if current user's data is included in selection
  const hasCurrentUserInSelection =
    currentUserId &&
    selectedRows.some((row) => row?.personalInfo?.publicId === currentUserId);

  // Check status of selected rows using the getStatus helper
  const hasActive =
    hasSelection && selectedRows.some((row) => getStatus(row) === "active");
  const hasInactive =
    hasSelection && selectedRows.some((row) => getStatus(row) === "inactive");

  const hasDraft =
    hasSelection && selectedRows.some((row) => getStatus(row) === "draft");
  const allActive =
    hasSelection && selectedRows.every((row) => getStatus(row) === "active");
  const allInactive =
    hasSelection && selectedRows.every((row) => getStatus(row) === "inactive");
  const allDraft =
    hasSelection && selectedRows.some((row) => getStatus(row) === "draft");

  const hasInvalidDraftMix =
    (hasDraft && (hasActive || hasInactive)) || (hasActive && hasInactive);

  const dropdownStyle = {
    position: "absolute",
    top: `${dropdownPosition.top}px`,
    left: `${dropdownPosition.left}px`,
    zIndex: 10,
    transform: isRTL ? "none" : "translateX(-100%)",
  };

  const handleHeaderMenuClick = () => {
    if (!grantPermission(`${permissionName}:delete`)) return;

    if (openHeaderMenu) {
      setOpenHeaderMenu(false);
    } else {
      const rect = threeDotsRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setOpenHeaderMenu(true);
    }
  };

  // Helper function to render disabled menu items with tooltip
  const renderDisabledItem = (text, tooltip) => (
    <div
      className={`${styles.dropdownItem} ${styles.disabledItem}`}
      title={tooltip}
    >
      <span className={styles.dropdownText}>{text}</span>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        threeDotsRef.current &&
        !threeDotsRef.current.contains(event.target)
      ) {
        setOpenHeaderMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.thThreeDots} ref={threeDotsRef}>
      <div onClick={handleHeaderMenuClick}>
        <img
          src={actionIcon || "/placeholder.svg"}
          alt="action icon"
          style={{
            opacity: grantPermission(`${permissionName}:delete`) ? 1 : 0.5,
            cursor: grantPermission(`${permissionName}:delete`)
              ? "pointer"
              : "not-allowed",
          }}
        />
      </div>

      {openHeaderMenu &&
        grantPermission(`${permissionName}:delete`) &&
        ReactDOM.createPortal(
          <div
            className={styles.dropdown}
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 10,
              transform: isRTL ? "none" : "translateX(-100%)", // Explicitly set transform
            }}
          >
            {!hasSelection ? (
              <div className={styles.dropdownItem}>
                <span className={styles.dropdownText} style={{ color: "#999" }}>
                  {t("Select rows to perform bulk actions")}
                </span>
              </div>
            ) : hasInvalidDraftMix ? (
              renderDisabledItem(
                t("Bulk actions ({{count}})", { count: selectedCount }),
                t("Cannot perform bulk actions on mixed statuses")
              )
            ) : (
              <>
                {isInvite &&
                  grantPermission(`${permissionName}:create`) &&
                  hasSelection &&
                  (allActive ? (
                    <div
                      className={styles.dropdownItem}
                      onClick={() => handleSendInvitation(selectedRows, true)}
                    >
                      <span className={styles.dropdownText}>
                        {invitationLabel
                          ? t(invitationLabel)
                          : t("Send Invitation")}{" "}
                        ({selectedCount})
                      </span>
                    </div>
                  ) : (
                    <div
                      className={`${styles.dropdownItem} ${styles.disabledItem}`}
                      title={t(
                        "Invitations can only be sent to active employees"
                      )}
                    >
                      <span className={styles.dropdownText}>
                        {invitationLabel
                          ? t(invitationLabel)
                          : t("Send Invitation")}{" "}
                        ({selectedCount})
                      </span>
                    </div>
                  ))}

                {allDraft &&
                  grantPermission(`${permissionName}:delete`) &&
                  (hasCurrentUserInSelection ? (
                    renderDisabledItem(
                      t("Clear from Draft ({{count}})", {
                        count: selectedCount,
                      }),
                      t(
                        "Cannot clear draft as your account is included in selection"
                      )
                    )
                  ) : (
                    <div
                      className={styles.dropdownItem}
                      onClick={() => handleBulkStatusUpdate("draft")}
                    >
                      <span className={styles.dropdownText}>
                        {t("Clear from Draft ({{count}})", {
                          count: selectedCount,
                        })}
                      </span>
                    </div>
                  ))}

                {(hasInactive || allInactive) &&
                  (hasCurrentUserInSelection ? (
                    renderDisabledItem(
                      t("Reactivate ({{count}})", { count: selectedCount }),
                      t(
                        "Cannot reactivate as your account is included in selection"
                      )
                    )
                  ) : (
                    <div
                      className={styles.dropdownItem}
                      onClick={() => handleBulkStatusUpdate("active")}
                    >
                      <span className={styles.dropdownText}>
                        {t("Reactivate ({{count}})", { count: selectedCount })}
                      </span>
                    </div>
                  ))}
                {(hasActive || allActive) &&
                  (hasCurrentUserInSelection ? (
                    renderDisabledItem(
                      t("Deactivate ({{count}})", { count: selectedCount }),
                      t(
                        "Cannot deactivate as your account is included in selection"
                      )
                    )
                  ) : (
                    <div
                      className={styles.dropdownItem}
                      onClick={() => handleBulkStatusUpdate("inactive")}
                    >
                      <span className={styles.dropdownText}>
                        {t("Deactivate ({{count}})", { count: selectedCount })}
                      </span>
                    </div>
                  ))}
                {isDelete &&
                  allInactive &&
                  (hasCurrentUserInSelection ? (
                    renderDisabledItem(
                      t("Delete ({{count}})", { count: selectedCount }),
                      t(
                        "Cannot delete as your account is included in selection"
                      )
                    )
                  ) : (
                    <div
                      className={`${styles.dropdownItem} ${styles.deleteItem}`}
                      onClick={() => handleBulkStatusUpdate("delete")}
                    >
                      <span className={styles.dropdownText}>
                        {t("Delete ({{count}})", { count: selectedCount })}
                      </span>
                    </div>
                  ))}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default HeaderActionItem;
